import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchDemoScenarios, getCurrentWeek, submitLaunchPartnerApplication } from '../lib/api';
import { getLoomEmbedUrl } from '../lib/utils';
import {
  HERO_HEADLINE_EMPHASIS,
  HERO_HEADLINE_PRIMARY,
  THOUGHTFABRIC_WEBSITE_URL,
} from './LiveDemoPage/constants';
import {
  buildHeroScenarioPreviews,
  findSharedScenarioQuestion,
  getDefaultHeroSubheadline,
  getLiveDemoOrgLabel,
  shouldUseDefaultSubheadline,
} from './LiveDemoPage/utils';
import HeroChatPreview from './LiveDemoPage/HeroChatPreview';
import KnowledgeTrustBar from './LiveDemoPage/KnowledgeTrustBar';
import ProblemSection from './LiveDemoPage/ProblemSection';
import ProgressDots from './LiveDemoPage/ProgressDots';
import SectionSpotlight from './LiveDemoPage/SectionSpotlight';
import SectionTryIt from './LiveDemoPage/SectionTryIt';
import SlideLayout from './LiveDemoPage/SlideLayout';
import useScrollSection from './LiveDemoPage/useScrollSection';

export default function LiveDemoPage({
  clientId,
  config,
  orgName,
  logoUrl,
  knowledgeTrust,
  onOpenChat,
  onAskQuestion,
}) {
  const [scenarioOptions, setScenarioOptions] = useState([]);
  const [heroScenarioOptions, setHeroScenarioOptions] = useState([]);
  const [contrastSharedQuestion, setContrastSharedQuestion] = useState('');
  const [shareCtaCopied, setShareCtaCopied] = useState(false);
  const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);
  const [applicationError, setApplicationError] = useState('');
  const [applicationSubmitted, setApplicationSubmitted] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    contactName: '',
    workEmail: '',
    organizationName: orgName || '',
    role: '',
    firstWorkflow: '',
  });
  const [spotlightWeek, setSpotlightWeek] = useState(null);
  const mainRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('live-demo-snap-page');
    return () => root.classList.remove('live-demo-snap-page');
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSpotlight() {
      try {
        const week = await getCurrentWeek(clientId);
        if (!cancelled) setSpotlightWeek(week);
      } catch (error) {
        if (!cancelled) setSpotlightWeek(null);
        console.warn('Spotlight unavailable for client:', clientId, error);
      }
    }

    loadSpotlight();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;

    async function loadScenarios() {
      try {
        const result = await fetchDemoScenarios(clientId, { refresh: true });
        if (!cancelled) {
          const scenarios = Array.isArray(result?.scenarios) ? result.scenarios : [];
          const heroScenarios = Array.isArray(result?.hero_demo_chat?.scenarios)
            ? result.hero_demo_chat.scenarios
            : [];
          setScenarioOptions(scenarios);
          setHeroScenarioOptions(heroScenarios);
          setContrastSharedQuestion(String(result?.shared_question || '').trim());
        }
      } catch (error) {
        if (!cancelled) {
          setScenarioOptions([]);
          setHeroScenarioOptions([]);
          setContrastSharedQuestion('');
        }
        console.error('Failed to load live demo scenarios:', error);
      }
    }

    loadScenarios();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const liveDemoOrgLabel = useMemo(() => getLiveDemoOrgLabel(orgName), [orgName]);
  const heroSubheadline = useMemo(
    () =>
      shouldUseDefaultSubheadline(config?.subheadline, orgName)
        ? getDefaultHeroSubheadline(orgName, knowledgeTrust?.domain)
        : config?.subheadline,
    [config?.subheadline, orgName, knowledgeTrust?.domain],
  );
  const sharedQuestion = useMemo(
    () =>
      String(contrastSharedQuestion || config?.shared_question || '').trim() ||
      findSharedScenarioQuestion(scenarioOptions) ||
      'When is my renewal due and what do I still need to do?',
    [contrastSharedQuestion, config?.shared_question, scenarioOptions],
  );
  const heroScenarioPreviews = useMemo(
    () => buildHeroScenarioPreviews(heroScenarioOptions),
    [heroScenarioOptions],
  );
  const embedUrl = getLoomEmbedUrl(config?.video_url);
  const closeCtaEyebrow = config?.close_cta_eyebrow || 'Founding partner program';
  const closeCtaHeadline = config?.close_cta_headline || 'Six months free for our first cohort.';
  const closeCtaBody =
    config?.close_cta_body ||
    "We're early. We can support a small group well — apply if you want to be in it. 48-hour reply.";
  const closeCtaLabel = config?.close_cta_label || 'Apply now';
  const closeCtaSecondaryLabel = config?.close_cta_secondary_label || 'Copy link for your team';
  const closeCtaUrl = config?.close_cta_url || 'https://calendly.com/alexdowbor/chat-with-alex';
  const closeCtaFollowupLabel = config?.close_cta_followup_label || 'Pick a time now';
  const closeCtaSuccessTitle = config?.close_cta_success_title || 'Application received';
  const closeCtaSuccessBody =
    config?.close_cta_success_body ||
    "Thanks. We'll review fit and follow up. If you already want to talk, you can pick a time now.";
  // Horizontal slideshow: page is locked to viewport height; <main> is the
  // horizontal scroll container; each <section> is a full-viewport-wide slide.
  const pageClass = 'flex h-screen flex-col overflow-hidden bg-tf-canvas font-brand text-tf-ink';
  const topBarClass = 'flex-shrink-0 border-b border-tf-stone bg-tf-canvas/85 backdrop-blur';
  const trackClass =
    'live-demo-snap-track flex flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden';
  const slideClass = 'flex h-full w-screen flex-shrink-0 snap-start snap-always overflow-y-auto';

  const spotlightSpots = useMemo(
    () => (Array.isArray(spotlightWeek?.spots) ? spotlightWeek.spots.slice(0, 3) : []),
    [spotlightWeek],
  );
  const hasSpotlight = spotlightSpots.length > 0;

  const sectionList = useMemo(() => {
    const list = [
      { id: 'hero', label: 'Welcome' },
      { id: 'trust-bar', label: 'Live content coverage' },
      { id: 'problem', label: 'The situation' },
    ];
    if (hasSpotlight) list.push({ id: 'spotlight', label: 'This week' });
    list.push({ id: 'try-it', label: 'Try it' });
    if (embedUrl) list.push({ id: 'video', label: 'Walkthrough' });
    list.push({ id: 'cta', label: 'Apply' });
    return list;
  }, [embedUrl, hasSpotlight]);

  const sectionIds = useMemo(() => sectionList.map((s) => s.id), [sectionList]);
  const activeSectionId = useScrollSection(sectionIds);

  function handleSectionJump(id) {
    const el = document.getElementById(id);
    if (!el) return;
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    el.scrollIntoView({ behavior: prefersReducedMotion ? 'instant' : 'smooth' });
  }

  // Keyboard navigation: PageUp/PageDown and Arrow keys jump between sections.
  // Skipped when focus is in a form field or content-editable area so the chat
  // input and the launch-partner form remain typeable.
  useEffect(() => {
    function handleKey(event) {
      const target = event.target;
      const isFormField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;
      if (isFormField) return;

      const currentIdx = sectionIds.indexOf(activeSectionId);
      if (currentIdx < 0) return;

      let nextIdx = currentIdx;
      if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === 'ArrowDown') {
        nextIdx = Math.min(currentIdx + 1, sectionIds.length - 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp' || event.key === 'ArrowUp') {
        nextIdx = Math.max(currentIdx - 1, 0);
      } else if (event.key === 'Home') {
        nextIdx = 0;
      } else if (event.key === 'End') {
        nextIdx = sectionIds.length - 1;
      } else {
        return;
      }

      if (nextIdx !== currentIdx) {
        event.preventDefault();
        handleSectionJump(sectionIds[nextIdx]);
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [sectionIds, activeSectionId]);

  // Two gesture profiles route through the same wheel handler:
  //
  // 1. Trackpad two-finger vertical scroll (deltaY values in the 1-50 range,
  //    fired every ~10ms). We translate the deltaY directly into horizontal
  //    scrollLeft, lightly amplified so a normal-sized swipe is enough to
  //    cross the snap midpoint. The browser's scroll-snap-stop: always then
  //    catches at the next slide boundary. This feels like the slides
  //    follow the user's fingers instead of jumping a fixed amount per
  //    gesture - the previous one-shot debounce was the "jumpy" feel the
  //    user noticed.
  //
  // 2. Mouse wheel click (single isolated event with |deltaY| around 100+).
  //    A proportional 300px scroll wouldn't cross the snap midpoint so the
  //    page would always snap back. For events with |deltaY| > 50 we treat
  //    each as one-slide advance with a short cooldown.
  //
  // Horizontal trackpad swipes (|deltaX| > |deltaY|) bypass JS entirely so
  // the browser's native snap handles them.
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;
    let lastWheelClickTime = 0;
    const TRACKPAD_AMPLIFY = 3;
    const MOUSE_WHEEL_THRESHOLD = 50;
    const MOUSE_WHEEL_COOLDOWN = 250;

    function handleWheel(event) {
      const dx = event.deltaX || 0;
      const dy = event.deltaY || 0;
      if (Math.abs(dy) <= Math.abs(dx)) return;
      if (dy === 0) return;

      // If the wheel originates inside a vertically-scrollable element that
      // still has room to scroll in the gesture direction, let native vertical
      // scroll happen instead of hijacking it for horizontal navigation. This
      // is what lets users read tall slide content (e.g. the application form
      // on the CTA slide). Once the inner scroll container hits its edge, the
      // wheel becomes a slide-advance again.
      let node = event.target;
      while (node && node !== el) {
        if (node.nodeType === 1) {
          const style = getComputedStyle(node);
          const overflowY = style.overflowY;
          const scrollable =
            (overflowY === 'auto' || overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight + 1;
          if (scrollable) {
            const atTop = node.scrollTop <= 0;
            const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
            const canScrollDown = dy > 0 && !atBottom;
            const canScrollUp = dy < 0 && !atTop;
            if (canScrollDown || canScrollUp) return;
            break;
          }
        }
        node = node.parentElement;
      }

      event.preventDefault();

      if (Math.abs(dy) <= MOUSE_WHEEL_THRESHOLD) {
        // Trackpad-style continuous gesture - proportional scroll
        el.scrollLeft += dy * TRACKPAD_AMPLIFY;
        return;
      }

      // Discrete mouse-wheel click - one slide advance with cooldown
      const now = Date.now();
      if (now - lastWheelClickTime < MOUSE_WHEEL_COOLDOWN) return;
      lastWheelClickTime = now;

      const currentIdx = sectionIds.indexOf(activeSectionId);
      if (currentIdx < 0) return;

      const nextIdx =
        dy > 0 ? Math.min(currentIdx + 1, sectionIds.length - 1) : Math.max(currentIdx - 1, 0);

      if (nextIdx !== currentIdx) {
        handleSectionJump(sectionIds[nextIdx]);
      }
    }

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [sectionIds, activeSectionId]);

  async function handleShareCta() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareCtaCopied(true);
      window.setTimeout(() => setShareCtaCopied(false), 1800);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  }

  function handleApplicationFieldChange(event) {
    const { name, value } = event.target;
    setApplicationForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function handleApplicationSubmit(event) {
    event.preventDefault();
    setApplicationError('');
    setIsSubmittingApplication(true);

    try {
      const result = await submitLaunchPartnerApplication({
        client_id: clientId,
        contact_name: applicationForm.contactName,
        work_email: applicationForm.workEmail,
        organization_name: applicationForm.organizationName,
        role: applicationForm.role,
        first_workflow: applicationForm.firstWorkflow,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
      });
      setApplicationSubmitted(result.application || { contact_name: applicationForm.contactName });
    } catch (error) {
      setApplicationError(error.message || 'Unable to submit application right now.');
    } finally {
      setIsSubmittingApplication(false);
    }
  }

  return (
    <div className={pageClass}>
      <div className={topBarClass}>
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 text-[13px] text-tf-ink/75 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:text-sm">
          <a
            href={THOUGHTFABRIC_WEBSITE_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Visit ThoughtFabric"
            className="inline-flex w-fit items-center rounded-md transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tf-teal/35 focus-visible:ring-offset-2 focus-visible:ring-offset-tf-canvas"
          >
            <img
              src="/thoughtfabric_wordmark.svg"
              alt="ThoughtFabric"
              className="h-[1.02rem] w-auto sm:h-[1.1rem]"
            />
          </a>

          <div className="flex min-w-0 items-center">
            <span className="truncate">Already running on {orgName} content</span>
          </div>
        </div>
      </div>

      <ProgressDots sections={sectionList} activeId={activeSectionId} onJump={handleSectionJump} />

      <main ref={mainRef} className={trackClass}>
        <section
          id="hero"
          className={`${slideClass} relative items-start bg-tf-canvas pb-10 pt-8 sm:pb-14 sm:pt-16 lg:items-center lg:pt-0`}
        >
          <div className="relative mx-auto my-auto w-full max-w-6xl px-4 sm:px-6">
            <div className="grid items-start gap-10 sm:gap-12 lg:grid-cols-[minmax(0,1fr)_520px] lg:items-center lg:gap-16">
              <div className="space-y-7 sm:space-y-8 lg:py-10">
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt={`${orgName} logo`}
                    className="h-auto max-h-14 max-w-[180px] object-contain opacity-90 sm:max-w-[210px]"
                  />
                )}

                <div className="space-y-5 sm:space-y-6">
                  <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-tf-teal">
                    {config?.eyebrow || 'ThoughtFabric Concierge'}
                  </p>
                  <h1 className="max-w-4xl font-brand text-[2.5rem] font-light leading-[1.05] tracking-[-0.02em] text-tf-ink sm:text-[3.5rem] lg:text-[4rem]">
                    <span className="block">
                      {config?.headline_primary || HERO_HEADLINE_PRIMARY}
                    </span>
                    <span className="block text-tf-teal">
                      {config?.headline_emphasis || HERO_HEADLINE_EMPHASIS}
                    </span>
                  </h1>
                  <p className="max-w-2xl text-[17px] font-light leading-[1.6] text-tf-ink/80 sm:text-[18px]">
                    {heroSubheadline}
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={onOpenChat}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-tf-teal px-6 py-3 text-[14px] font-medium tracking-[0.01em] text-white transition-colors hover:bg-tf-teal-mid sm:w-auto"
                  >
                    {config?.cta_label || 'Chat with Concierge'}
                    <span aria-hidden="true">→</span>
                  </button>
                </div>
              </div>

              <HeroChatPreview
                scenarioPreviews={heroScenarioPreviews}
                onAskQuestion={onAskQuestion}
              />
            </div>
          </div>
        </section>

        <section id="trust-bar" className={slideClass}>
          <KnowledgeTrustBar knowledgeTrust={knowledgeTrust} orgName={orgName} />
        </section>

        <section id="problem" className={slideClass}>
          <ProblemSection config={config} />
        </section>

        {hasSpotlight && (
          <section id="spotlight" className={slideClass}>
            <SectionSpotlight
              week={spotlightWeek}
              spots={spotlightSpots}
              onAskQuestion={onAskQuestion}
            />
          </section>
        )}

        <section id="try-it" className={slideClass}>
          <SectionTryIt
            headline={config?.contrast_headline || 'Same question. Different guidance per member.'}
            intro={
              config?.contrast_intro ||
              'The unlock for renewal recovery and CE compliance — context shifts the answer.'
            }
            scenarios={scenarioOptions}
            sharedQuestion={sharedQuestion}
            onAskQuestion={onAskQuestion}
          />
        </section>

        {embedUrl && (
          <section id="video" className={slideClass}>
            <SlideLayout
              eyebrow="Watch the walkthrough"
              title={config?.video_label || 'See it work end-to-end.'}
            >
              <div className="overflow-hidden border border-tf-stone bg-tf-surface">
                <iframe
                  src={embedUrl}
                  className="aspect-video w-full"
                  title={`${orgName} live demo video`}
                  allowFullScreen
                />
              </div>
            </SlideLayout>
          </section>
        )}

        <section id="cta" className={slideClass}>
          <SlideLayout
            eyebrow={closeCtaEyebrow}
            title={closeCtaHeadline}
            intro={closeCtaBody}
            footer={
              <span className="block text-tf-ink/50">
                This is a custom ThoughtFabric demo for {liveDemoOrgLabel}, built from their
                publicly available content. Example member records on this page are demo scenarios,
                not live private accounts.
              </span>
            }
          >
            {applicationSubmitted ? (
              <div className="max-w-2xl border border-tf-stone bg-tf-surface px-5 py-5 sm:px-6">
                <p className="text-[15px] font-medium text-tf-ink">{closeCtaSuccessTitle}</p>
                <p className="mt-2 text-[15px] font-light leading-[1.7] text-tf-ink/75">
                  {closeCtaSuccessBody}
                </p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  <a
                    href={closeCtaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex w-full items-center justify-center rounded-md bg-tf-teal px-6 py-3 text-[14px] font-medium tracking-[0.01em] text-white transition-colors hover:bg-tf-teal-mid sm:w-auto"
                  >
                    {closeCtaFollowupLabel}
                  </a>
                  <p className="text-[14px] font-light text-tf-ink/55">
                    Submitted for {applicationSubmitted.contact_name || applicationForm.contactName}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleApplicationSubmit} className="max-w-3xl">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="space-y-2">
                    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-tf-ink/55">
                      Name
                    </span>
                    <input
                      type="text"
                      name="contactName"
                      value={applicationForm.contactName}
                      onChange={handleApplicationFieldChange}
                      className="w-full border border-tf-stone bg-tf-surface px-3 py-2.5 text-[14px] font-light text-tf-ink outline-none transition-colors placeholder:text-tf-ink/40 focus:border-tf-teal"
                      placeholder="Your name"
                      required
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-tf-ink/55">
                      Work email
                    </span>
                    <input
                      type="email"
                      name="workEmail"
                      value={applicationForm.workEmail}
                      onChange={handleApplicationFieldChange}
                      className="w-full border border-tf-stone bg-tf-surface px-3 py-2.5 text-[14px] font-light text-tf-ink outline-none transition-colors placeholder:text-tf-ink/40 focus:border-tf-teal"
                      placeholder="you@organization.org"
                      required
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-tf-ink/55">
                      Organization
                    </span>
                    <input
                      type="text"
                      name="organizationName"
                      value={applicationForm.organizationName}
                      onChange={handleApplicationFieldChange}
                      className="w-full border border-tf-stone bg-tf-surface px-3 py-2.5 text-[14px] font-light text-tf-ink outline-none transition-colors placeholder:text-tf-ink/40 focus:border-tf-teal"
                      placeholder={orgName}
                      required
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-tf-ink/55">
                      Role
                    </span>
                    <input
                      type="text"
                      name="role"
                      value={applicationForm.role}
                      onChange={handleApplicationFieldChange}
                      className="w-full border border-tf-stone bg-tf-surface px-3 py-2.5 text-[14px] font-light text-tf-ink outline-none transition-colors placeholder:text-tf-ink/40 focus:border-tf-teal"
                      placeholder="e.g. Director of People & Culture"
                      required
                    />
                  </label>
                </div>
                <label className="mt-5 block space-y-2">
                  <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-tf-ink/55">
                    What would you want to improve first — and who else needs to weigh in?
                  </span>
                  <textarea
                    name="firstWorkflow"
                    value={applicationForm.firstWorkflow}
                    onChange={handleApplicationFieldChange}
                    className="min-h-[88px] w-full border border-tf-stone bg-tf-surface px-3 py-2.5 text-[14px] font-light text-tf-ink outline-none transition-colors placeholder:text-tf-ink/40 focus:border-tf-teal"
                    placeholder="e.g. recertification, member onboarding, compliance support"
                    required
                  />
                </label>
                {applicationError ? (
                  <p className="mt-3 text-[14px] font-light text-tf-terracotta-mid">
                    {applicationError}
                  </p>
                ) : null}
                <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
                  <button
                    type="submit"
                    disabled={isSubmittingApplication}
                    className="inline-flex w-full items-center justify-center rounded-md bg-tf-teal px-6 py-3 text-[14px] font-medium tracking-[0.01em] text-white transition-colors hover:bg-tf-teal-mid disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {isSubmittingApplication ? 'Submitting…' : closeCtaLabel}
                  </button>
                  <button
                    type="button"
                    onClick={handleShareCta}
                    className="inline-flex items-center gap-1.5 text-[14px] font-medium text-tf-teal underline-offset-4 transition-colors hover:text-tf-teal-mid hover:underline"
                  >
                    {shareCtaCopied ? 'Link copied' : closeCtaSecondaryLabel}
                  </button>
                </div>
              </form>
            )}
          </SlideLayout>
        </section>
      </main>
    </div>
  );
}
